import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CheckboxComponent } from '@enginuity/core/molecules/checkbox/checkbox.component';
import { TableDataComponent } from '../table-data/table-data.component';
import { ActionBtnComponent } from '@enginuity/core/molecules/action-btn/action-btn.component';
import { TableView } from '@services/ui-services/models/table-view';

import { TablePropertyComponent } from '../table-property/table-property.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { NoDataComponent } from '../no-data/no-data.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ProcessingState } from '@services/core-services/models/processing-state';
import { ErrorStateComponent } from '../error-state/error-state.component';
import { Query } from '@services/core-services/models';
import {
  ContextRegistryService,
  ContextProvider,
  TableContext,
} from '@services/core-services/context-registry.service';

@Component({
  selector: 'app-table-view',
  templateUrl: './table-view.component.html',
  styleUrls: ['./table-view.component.scss'],
  imports: [
    CommonModule,

    CheckboxComponent,
    TableDataComponent,
    ActionBtnComponent,
    TablePropertyComponent,
    OverlayModule,
    NoDataComponent,
    NgxSkeletonLoaderModule,
    ErrorStateComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableViewComponent
  implements OnChanges, OnInit, OnDestroy, ContextProvider<TableContext>
{
  protected readonly ProcessingState = ProcessingState;
  protected isOpen = false;
  private contextId: string;
  @Input() headerChecked: boolean = false;
  protected skeleton = new Array(15);
  @Input() highlightedRowIndex: number | null = null;
  @Output() onClick = new EventEmitter<object>();
  @Output() onChange = new EventEmitter<any>();
  @Output() onSort = new EventEmitter<object>();
  @Input() compactTable: boolean = false;
  @Input() logStyle: boolean = false;
  @Input() tableResponsive: boolean = true;
  @Input() query!: Query | undefined | null;
  @Input() loader: ProcessingState | null = ProcessingState.Loading;
  @Input() errorMessage: string | null = '';
  @Input() noDataMessage: string = 'No data available';

  // Flag to prevent external state override during internal state changes
  private isInternalStateChange = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('table_schema') tableView: TableView = {
    head: [
      { type: 'checkbox', label: '', isShow: true, sortOrder: 'asc' },
      { type: 'customer', label: 'Name', isShow: true, sortOrder: 'asc' },
      {
        type: 'string',
        label: 'Business Name',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'string',
        label: 'Business Type',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'segment',
        label: 'segment',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'string',
        label: 'member since',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'string',
        label: 'Last seen',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'status',
        label: 'Status',
        sortable: true,
        isShow: true,
        sortOrder: 'asc',
      },
      {
        type: 'actions',
        label: '',
        sortable: false,
        icon: '',
        isShow: true,
        sortOrder: 'asc',
      },
    ],
    rows: [
      [
        { type: 'checkbox', label: '' },
        {
          type: 'customer',
          label: 'Name',
          customer: {
            name: 'John',
          },
        },
        {
          type: 'text',
          text: {
            title: 'JSR Tradig',
          },
        },
        {
          type: 'text',
          text: {
            title: 'Retailer',
          },
        },
        {
          type: 'segments',
          label: 'segment',
          segment: {},
        },
        {
          type: 'text',
          text: {
            title: '15 May 2020',
          },
        },
        {
          type: 'text',
          label: 'Last seen',
          text: {
            title: '15 May 2020 8:30 am',
          },
        },
        {
          type: 'status',
          label: 'Status',
          status: {
            label: 'Super saver price',
            Type: 'completed',
            IconType: 'no_icon',
          },
        },
        { type: 'actions', label: '', icon: '' },
      ],
      [
        { type: 'checkbox', label: '' },
        {
          type: 'customer',
          label: 'Name',
          customer: {
            name: 'John',
          },
        },
        {
          type: 'text',
          text: {
            title: 'JSR Tradig',
          },
        },
        {
          type: 'text',
          text: {
            title: 'Retailer',
          },
        },
        {
          type: 'segments',
          label: 'segment',
          segment: {},
        },
        {
          type: 'text',
          text: {
            title: '15 May 2020',
          },
        },
        {
          type: 'text',
          label: 'Last seen',
          text: {
            title: '15 May 2020 8:30 am',
          },
        },
        {
          type: 'status',
          label: 'Status',
          status: {
            label: 'Super saver price',
            Type: 'completed',
            IconType: 'no_icon',
            uppercase: false,
            copymode: false,
          },
        },
        {
          type: 'actions',
          label: '',
          actions: [
            {
              icon: 'more',
              text: '',
              active: false,
            },
            {
              icon: 'chevron_right',
              text: '',
              active: false,
            },
          ],
        },
      ],
      [
        { type: 'checkbox', label: '' },
        {
          type: 'customer',
          label: 'Name',
          customer: {
            name: 'John',
          },
        },
        {
          type: 'text',
          text: {
            title: 'JSR Tradig',
          },
        },
        {
          type: 'text',
          text: {
            title: 'Retailer',
          },
        },
        {
          type: 'segments',
          label: 'segment',
          segment: {},
        },
        {
          type: 'text',
          text: {
            title: '15 May 2020',
          },
        },
        {
          type: 'text',
          label: 'Last seen',
          text: {
            title: '15 May 2020 8:30 am',
          },
        },
        {
          type: 'status',
          label: 'Status',
          status: {
            label: 'Super saver price',
            Type: 'completed',
            IconType: 'no_icon',
          },
        },
        {
          type: 'actions',
          label: '',
          actions: [
            {
              icon: 'more',
              text: '',
              active: false,
            },
            {
              icon: 'chevron_right',
              text: '',
              active: false,
            },
          ],
        },
      ],
    ],
    data: [{}],
  };

  constructor(
    private contextRegistry: ContextRegistryService,
    private cdr: ChangeDetectorRef
  ) {
    // Generate unique context ID for this table instance
    this.contextId = `table-${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnInit(): void {
    // Register this table as a context provider
    this.contextRegistry.registerContext(this.contextId, this);
  }

  ngOnDestroy(): void {
    // Clean up context registration
    this.contextRegistry.unregisterContext(this.contextId);
  }

  // Focus detection to set this table as active context
  @HostListener('click')
  onHostClick() {
    this.contextRegistry.setActiveContext(this.contextId);
  }

  @HostListener('focusin')
  onHostFocusIn() {
    this.contextRegistry.setActiveContext(this.contextId);
  }

  // ContextProvider interface implementation
  getContext(): TableContext {
    const checkboxColumn = this.tableView.head.find(col => col.type === 'checkbox');
    const hasSelectionEnabled = checkboxColumn?.isShow === true;

    if (!hasSelectionEnabled) {
      return {
        id: this.contextId,
        name: 'Table',
        type: 'table',
        hasSelection: false,
        selectedCount: 0,
        totalCount: this.tableView.rows.length,
        canSelectAll: false,
        selectAll: () => {},
        deselectAll: () => {},
        clearSelection: () => {},
        isAllSelected: () => false,
        hasPartialSelection: () => false,
      };
    }

    const selectedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && col.isChecked)
    );

    return {
      id: this.contextId,
      name: 'Table',
      type: 'table',
      hasSelection: selectedRows.length > 0,
      selectedCount: selectedRows.length,
      totalCount: this.tableView.rows.length,
      canSelectAll: hasSelectionEnabled,
      selectAll: () => this.selectAllRows(),
      deselectAll: () => this.deselectAllRows(),
      clearSelection: () => this.clearSelection(),
      isAllSelected: () =>
        selectedRows.length === this.tableView.rows.length && this.tableView.rows.length > 0,
      hasPartialSelection: () =>
        selectedRows.length > 0 && selectedRows.length < this.tableView.rows.length,
    };
  }

  isActive(): boolean {
    // Table is active if it has data and selection is enabled
    const checkboxColumn = this.tableView.head.find(col => col.type === 'checkbox');
    return this.tableView.rows.length > 0 && checkboxColumn?.isShow === true;
  }

  // Table action methods
  private selectAllRows(): void {
    // Set flag to indicate internal state change is happening
    const wasInternalStateChange = this.isInternalStateChange;
    this.isInternalStateChange = true;

    // Select all rows
    this.resetHeaderCheckbox(true, true);
    this.onClick.emit({ action: 'header-checkbox', value: true });

    // Restore previous state
    this.isInternalStateChange = wasInternalStateChange;
    this.cdr.detectChanges();
  }

  private deselectAllRows(): void {
    // Set flag to indicate internal state change is happening
    const wasInternalStateChange = this.isInternalStateChange;
    this.isInternalStateChange = true;

    this.resetHeaderCheckbox(false, true);
    this.onClick.emit({ action: 'header-checkbox', value: false });

    // Restore previous state
    this.isInternalStateChange = wasInternalStateChange;
    this.cdr.detectChanges();
  }

  private clearSelection(): void {
    // Set flag to indicate internal state change is happening
    const wasInternalStateChange = this.isInternalStateChange;
    this.isInternalStateChange = true;

    this.resetHeaderCheckbox(false, true);
    this.onClick.emit({ action: 'header-checkbox', value: false });

    // Restore previous state
    this.isInternalStateChange = wasInternalStateChange;
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only reset checkboxes from external changes if it's not an internal state change
    if (
      changes['headerChecked'] &&
      !changes['headerChecked'].firstChange &&
      !this.isInternalStateChange
    ) {
      this.resetHeaderCheckbox(changes['headerChecked'].currentValue);
    }
    if (
      changes['query'] &&
      changes['query'].previousValue?.page !== changes['query'].currentValue?.page
    ) {
      this.resetHeaderCheckbox(false);
    }
    if (changes['tableView'] && this.headerChecked === false) {
      this.resetHeaderCheckbox(false);
    }
    const query = (changes['query']?.currentValue as Query) || this.query;
    const tableView = (changes['tableView']?.currentValue as Query) || this.tableView;

    if (!tableView || !query?.sort_by) return;

    const keys = Object.keys(query.sort_by || {});
    this.tableView.head = this.tableView.head.map(col => {
      const key = col.column || col.name;
      return {
        ...col,
        isSortActive: keys.includes(key || ''),
        sortOrder: query.sort_by![key as string],
      };
    });
  }

  sort(col: any, emit?: boolean) {
    /**
     * sort order
     * default
     * asc
     * desc
     */

    col.isSortActive = true;
    switch (col.sortOrder) {
      case 'default':
        col.sortOrder = 'desc';
        break;
      case 'desc':
        col.sortOrder = 'asc';
        break;
      case 'asc':
        col.sortOrder = 'default';
        col.isSortActive = false;
        break;

      default:
        col.sortOrder = 'desc';
        break;
    }

    const sortList: any = {};
    const activeSortList = this.tableView.head.filter((x: any) => x.isSortActive);

    activeSortList.forEach((element: any) => {
      sortList[element.column] = element.sortOrder;
    });

    if (emit) {
      this.onSort.emit(sortList);
    }
  }

  showColumn(columnName: string) {
    return this.tableView.head.find((x: any) => x.column === columnName)?.isShow || false;
  }

  onCheckboxClick(eventName: any, column: any, value: boolean) {
    this.isInternalStateChange = true;

    if (eventName === 'row-checkbox') {
      this.onClick.emit({ action: eventName, value: column });
      // Update header checkbox state after row selection changes
      this.updateHeaderCheckboxState();
    } else if (eventName === 'header-checkbox') {
      this.tableView.rows.forEach((row: any) => {
        row.forEach((col: any) => {
          if (col.type === 'checkbox') {
            col.isChecked = value;
          }
        });
      });
      this.onClick.emit({ action: eventName, value });
      // Set header checkbox state directly for header clicks
      const checkboxColumn = this.tableView.head.find(col => col.type === 'checkbox');
      if (checkboxColumn) {
        checkboxColumn.isChecked = value;
        checkboxColumn.isCheckIndeterminate = false;
      }
    }

    if (column?.['onChange']) {
      column?.['onChange']?.(value);
    }

    // Reset the flag after a short delay to allow external state to update
    setTimeout(() => {
      this.isInternalStateChange = false;
    }, 0);

    this.cdr.detectChanges();
  }

  updateHeaderCheckboxState() {
    const checkedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && col.isChecked)
    );

    const uncheckedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && !col.isChecked)
    );

    this.tableView.head.forEach((col: any) => {
      if (col.type === 'checkbox') {
        col.isChecked =
          checkedRows.length === this.tableView.rows.length && this.tableView.rows.length > 0;
        col.isCheckIndeterminate = checkedRows.length > 0 && uncheckedRows.length > 0;
      }
    });

    this.cdr.detectChanges();
  }

  resetHeaderCheckbox(value: boolean, forceReset: boolean = false) {
    // Only reset if this is not an internal state change, unless forceReset is true
    if (this.isInternalStateChange && !forceReset) {
      return;
    }

    // Set header checkbox state
    this.tableView.head.forEach((col: any) => {
      if (col.type === 'checkbox') {
        col.isChecked = value;
        col.isCheckIndeterminate = false;
      }
    });

    // Set all row checkbox states
    this.tableView.rows.forEach((row: any) => {
      row.forEach((col: any) => {
        if (col.type === 'checkbox') {
          col.isChecked = value;
        }
      });
    });

    this.cdr.detectChanges();
  }

  get getIndeterminate() {
    // Always calculate indeterminate state based on actual checkbox states
    const uncheckedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && !col.isChecked)
    );
    const checkedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && col.isChecked)
    );
    return uncheckedRows.length !== 0 && checkedRows.length !== 0;
  }

  get getHeadCheck() {
    const checkedRows = this.tableView.rows.filter((row: any) =>
      row.some((col: any) => col.type === 'checkbox' && col.isChecked)
    );
    return checkedRows.length === this.tableView.rows.length && this.tableView.rows.length > 0;
  }

  handlePropertyChange(prop: any) {
    this.tableView.head = this.tableView.head.filter((x: any) => {
      return x.column === prop.column ? { ...x, ...prop } : x;
    });

    this.tableView.provider?.saveContext({ view: this.tableView });
  }

  trackByColumn(index: number, column: any): string {
    // Use a unique identifier for columns, such as 'id' or 'label'
    return column.id || column.label || index.toString();
  }

  trackByRow(index: number, row: any): string {
    // Use a unique identifier for rows, such as 'id' or combine properties
    return row.id || `row-${index}`;
  }
}
